import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiArrowLeft } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import Modal from "../../Modal/Modal";
import "../../../Styles/LoginPopup.css";
import PhoneKycPopup from "./PhoneKycPopup";
import axiosInstance from "../../../utils/AxiosConfig";
import { showError, showSuccess } from "../../../utils/Toast";
import loginimage from "../../../assests/Elumia_Login_banner.png";
import { completeBuyerAuthentication } from "../../../utils/buyerAuthFlow";

const OTP_DURATION_SECONDS = 10 * 60;
const GOOGLE_UNAVAILABLE_MESSAGE =
  "Google login is currently unavailable. Please use email or OTP login.";
const DEFAULT_GOOGLE_CALLBACK_PATH = "/auth/google/callback";
const PASSWORD_REGEX = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&]).{8,}$/;
const PASSWORD_VALIDATION_MESSAGE =
  "Password must be at least 8 characters and include letters, numbers and special characters.";

const normalizeGoogleRedirectUri = (value) => {
  const normalizedValue = String(value || "").trim();

  if (!normalizedValue) {
    return "";
  }

  try {
    const baseOrigin =
      typeof window !== "undefined"
        ? window.location.origin
        : "https://dev.elumiamedstore.com";
    const redirectUrl = new URL(normalizedValue, baseOrigin);

    if (redirectUrl.pathname.length > 1) {
      redirectUrl.pathname = redirectUrl.pathname.replace(/\/+$/, "");
    }

    return redirectUrl.toString();
  } catch {
    return normalizedValue.replace(/\/+$/, "");
  }
};

const resolveGoogleRedirectUri = () => {
  const configuredRedirectUri = normalizeGoogleRedirectUri(
    process.env.REACT_APP_GOOGLE_REDIRECT_URI || "",
  );

  if (configuredRedirectUri) {
    return configuredRedirectUri;
  }

  if (typeof window !== "undefined") {
    return normalizeGoogleRedirectUri(
      `${window.location.origin}${DEFAULT_GOOGLE_CALLBACK_PATH}`,
    );
  }

  return DEFAULT_GOOGLE_CALLBACK_PATH;
};

const isGoogleCallbackRoute = (redirectUri) => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const redirectUrl = new URL(redirectUri, window.location.origin);
    return redirectUrl.pathname === window.location.pathname;
  } catch {
    return window.location.pathname === DEFAULT_GOOGLE_CALLBACK_PATH;
  }
};

function LoginPopup({ isOpen, onClose }) {
  const [step, setStep] = useState("entry");
  const [authMode, setAuthMode] = useState("otp");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [kycOpen, setKycOpen] = useState(false);
  const [timer, setTimer] = useState(OTP_DURATION_SECONDS);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [loginMethod, setLoginMethod] = useState("email");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState("");
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotVerifying, setForgotVerifying] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const navigate = useNavigate();
  const onCloseRef = useRef(onClose);
  const finishLoginRef = useRef();

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const googleClientId = (process.env.REACT_APP_GOOGLE_CLIENT_ID || "").trim();
  const googleRedirectUri = resolveGoogleRedirectUri();

  const getLoginIdentifier = () =>
    loginMethod === "email" ? email.trim().toLowerCase() : mobile.trim();

  const resetForgotPasswordState = useCallback(() => {
    setForgotEmail("");
    setForgotOtp("");
    setForgotNewPassword("");
    setForgotConfirmPassword("");
    setForgotSending(false);
    setForgotVerifying(false);
    setResettingPassword(false);
  }, []);

  const resetPopupState = useCallback(() => {
    setStep("entry");
    setAuthMode("otp");
    setEmail("");
    setMobile("");
    setPassword("");
    setOtp("");
    setTimer(OTP_DURATION_SECONDS);
    setSending(false);
    setVerifying(false);
    setLoggingIn(false);
    setGoogleLoading(false);
    setLoginMethod("email");
    resetForgotPasswordState();
  }, [resetForgotPasswordState]);

  useEffect(() => {
    if (!isOpen) {
      resetPopupState();
    }
  }, [isOpen, resetPopupState]);

  useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return undefined;
    }

    const { body, documentElement } = document;
    const previousBodyOverflow = body.style.overflow;
    const previousHtmlOverflow = documentElement.style.overflow;

    body.classList.add("login-popup-open");
    body.style.overflow = "hidden";
    documentElement.style.overflow = "hidden";

    return () => {
      body.classList.remove("login-popup-open");
      body.style.overflow = previousBodyOverflow;
      documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [isOpen]);

  const finishLogin = useCallback(
    (payload, successMessage) => {
      completeBuyerAuthentication(payload, {
        successMessage: successMessage || "Login successful",
        onBeforeRedirect: () => onCloseRef.current?.(),
      });
    },
    [],
  );

  useEffect(() => {
    finishLoginRef.current = finishLogin;
  }, [finishLogin]);

  const requestLoginOtp = async (identifier) =>
    axiosInstance.post("login", { email: identifier });

  const requestPasswordLogin = async (identifier, passwordValue) =>
    axiosInstance.post("login-password", {
      identifier,
      password: passwordValue,
    });

  const requestForgotPasswordOtp = async (targetEmail) =>
    axiosInstance.post("forgotPassword", { email: targetEmail });

  const requestVerifyResetOtp = async (targetEmail, otpValue) =>
    axiosInstance.post("verifyResetOtp", {
      email: targetEmail,
      otp: otpValue,
    });

  const requestResetPassword = async (targetEmail, otpValue, newPassword) =>
    axiosInstance.post("resetPassword", {
      email: targetEmail,
      otp: otpValue,
      newPassword,
    });

  useEffect(() => {
    const handleGoogleCodeCallback = async () => {
      if (!isGoogleCallbackRoute(googleRedirectUri)) {
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const error = params.get("error");
      const code = params.get("code");

      if (error) {
        window.history.replaceState({}, document.title, window.location.pathname);
        showError("Google sign-in was cancelled. Please try again.");
        return;
      }

      if (!code) return;

      try {
        setGoogleLoading(true);

        const result = await axiosInstance.post("googleloginnew", {
          code,
          redirectUri: googleRedirectUri,
        });

        const payload = result?.data;

        if (payload?.token) {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname,
          );
          finishLoginRef.current?.(
            payload,
            payload.message || "Google login successful",
          );
        } else {
          showError("Invalid login response");
        }
      } catch (err) {
        console.error(err);
        showError(
          err.response?.data?.message || "Google login failed. Please try again.",
        );
      } finally {
        setGoogleLoading(false);
      }
    };

    handleGoogleCodeCallback();
  }, [googleRedirectUri]);

  useEffect(() => {
    if ((step === "otp" || step === "forgotOtp") && timer > 0) {
      const intervalId = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(intervalId);
    }

    return undefined;
  }, [step, timer]);

  const handleGoogleLoginClick = () => {
    if (!googleClientId) {
      showError(GOOGLE_UNAVAILABLE_MESSAGE);
      return;
    }

    const redirectUri = encodeURIComponent(googleRedirectUri);
    const scope = encodeURIComponent("openid email profile");

    const googleAuthUrl =
      `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${googleClientId}` +
      `&redirect_uri=${redirectUri}` +
      `&response_type=code` +
      `&scope=${scope}` +
      `&access_type=offline` +
      `&prompt=consent`;

    console.log("[GOOGLE LOGIN] redirectUri:", googleRedirectUri);
    console.log("[GOOGLE LOGIN] googleAuthUrl:", googleAuthUrl);

    window.location.href = googleAuthUrl;
  };

  const handleSendOtp = async () => {
    const identifier = getLoginIdentifier();

    if (loginMethod === "email") {
      if (!identifier) return showError("Enter email ID first.");
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier)) {
        return showError("Enter a valid email ID");
      }
    } else {
      if (!identifier) return showError("Enter mobile number first.");
      if (identifier.length < 10) return showError("Enter valid mobile number");
    }

    try {
      setSending(true);
      const response = await requestLoginOtp(identifier);
      setOtp("");
      setStep("otp");
      setTimer(OTP_DURATION_SECONDS);
      showSuccess(response?.data?.message || "OTP sent successfully");
    } catch (err) {
      showError(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    const identifier = getLoginIdentifier();

    if (!/^\d{6}$/.test(otp)) {
      return showError("Enter a valid 6 digit OTP");
    }

    try {
      setVerifying(true);

      const response = await axiosInstance.post("verifyOtp", {
        email: identifier,
        otp,
      });

      const payload = response?.data;

      if (payload?.token) {
        finishLogin(payload, payload.message || "Login successful");
      } else {
        showError("Login failed: invalid response");
      }
    } catch (err) {
      showError(
        err.response?.data?.message ||
          "OTP verification failed. Please try again",
      );
    } finally {
      setVerifying(false);
    }
  };

  const handlePasswordLogin = async () => {
    const identifier = getLoginIdentifier();

    if (loginMethod === "email") {
      if (!identifier) return showError("Enter email ID first.");
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier)) {
        return showError("Enter a valid email ID");
      }
    } else {
      if (!identifier) return showError("Enter mobile number first.");
      if (identifier.length < 10) return showError("Enter valid mobile number");
    }

    if (!password) {
      return showError("Enter your password");
    }

    try {
      setLoggingIn(true);
      const response = await requestPasswordLogin(identifier, password);
      const payload = response?.data;

      if (payload?.token) {
        finishLogin(payload, payload.message || "Login successful");
      } else {
        showError("Login failed: invalid response");
      }
    } catch (err) {
      showError(
        err.response?.data?.message || "Invalid email/mobile or password",
      );
    } finally {
      setLoggingIn(false);
    }
  };

  const handleResendOtp = async () => {
    const identifier = getLoginIdentifier();

    if (loginMethod === "email") {
      if (!identifier) return showError("Enter email ID first.");
    } else if (!identifier) {
      return showError("Enter mobile number first.");
    }

    try {
      setSending(true);
      const response = await requestLoginOtp(identifier);
      setOtp("");
      setTimer(OTP_DURATION_SECONDS);
      showSuccess(response?.data?.message || "OTP resent successfully");
    } catch (err) {
      showError(err.response?.data?.message || "Failed to resend OTP");
    } finally {
      setSending(false);
    }
  };

  const handleForgotPasswordClick = () => {
    setForgotEmail(loginMethod === "email" ? email.trim().toLowerCase() : "");
    setForgotOtp("");
    setForgotNewPassword("");
    setForgotConfirmPassword("");
    setTimer(OTP_DURATION_SECONDS);
    setStep("forgotEmail");
  };

  const handleSendForgotPasswordOtp = async () => {
    const normalizedEmail = forgotEmail.trim().toLowerCase();

    if (!normalizedEmail) {
      return showError("Enter your email address");
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      return showError("Enter a valid email ID");
    }

    try {
      setForgotSending(true);
      const response = await requestForgotPasswordOtp(normalizedEmail);
      setForgotEmail(normalizedEmail);
      setForgotOtp("");
      setStep("forgotOtp");
      setTimer(OTP_DURATION_SECONDS);
      showSuccess(response?.data?.message || "OTP sent to your email");
    } catch (err) {
      showError(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setForgotSending(false);
    }
  };

  const handleVerifyForgotPasswordOtp = async () => {
    const normalizedEmail = forgotEmail.trim().toLowerCase();

    if (!normalizedEmail) {
      return showError("Enter your email address");
    }

    if (!/^\d{6}$/.test(forgotOtp)) {
      return showError("Enter a valid 6 digit OTP");
    }

    try {
      setForgotVerifying(true);
      const response = await requestVerifyResetOtp(normalizedEmail, forgotOtp);
      setStep("forgotReset");
      showSuccess(response?.data?.message || "OTP verified successfully");
    } catch (err) {
      showError(err.response?.data?.message || "OTP verification failed");
    } finally {
      setForgotVerifying(false);
    }
  };

  const handleResendForgotPasswordOtp = async () => {
    const normalizedEmail = forgotEmail.trim().toLowerCase();

    if (!normalizedEmail) {
      return showError("Enter your email address");
    }

    try {
      setForgotSending(true);
      const response = await requestForgotPasswordOtp(normalizedEmail);
      setForgotOtp("");
      setTimer(OTP_DURATION_SECONDS);
      showSuccess(response?.data?.message || "OTP resent successfully");
    } catch (err) {
      showError(err.response?.data?.message || "Failed to resend OTP");
    } finally {
      setForgotSending(false);
    }
  };

  const handleResetPassword = async () => {
    const normalizedEmail = forgotEmail.trim().toLowerCase();

    if (!normalizedEmail) {
      return showError("Enter your email address");
    }

    if (!PASSWORD_REGEX.test(forgotNewPassword)) {
      return showError(PASSWORD_VALIDATION_MESSAGE);
    }

    if (forgotNewPassword !== forgotConfirmPassword) {
      return showError("Passwords do not match");
    }

    try {
      setResettingPassword(true);
      const response = await requestResetPassword(
        normalizedEmail,
        forgotOtp,
        forgotNewPassword,
      );
      showSuccess(response?.data?.message || "Password reset successful");
      setAuthMode("password");
      setLoginMethod("email");
      setEmail(normalizedEmail);
      setPassword("");
      setOtp("");
      setStep("entry");
      resetForgotPasswordState();
    } catch (err) {
      showError(err.response?.data?.message || "Failed to reset password");
    } finally {
      setResettingPassword(false);
    }
  };

  const clicksignup = () => {
    navigate("/signup");
    onClose();
  };

  const handleBack = () => {
    if (step === "otp") {
      setOtp("");
      setStep("entry");
      return;
    }

    if (step === "forgotEmail") {
      setStep("entry");
      return;
    }

    if (step === "forgotOtp") {
      setStep("forgotEmail");
      return;
    }

    if (step === "forgotReset") {
      setStep("forgotOtp");
    }
  };

  const toggleLoginMethod = () => {
    setLoginMethod(loginMethod === "email" ? "mobile" : "email");
    setEmail("");
    setMobile("");
    setPassword("");
    setOtp("");
  };

  const handleAuthModeChange = (nextMode) => {
    if (nextMode === authMode) return;

    setAuthMode(nextMode);
    setPassword("");
    setOtp("");
    setStep("entry");
  };

  const getHeaderTitle = () => {
    if (
      step === "forgotEmail" ||
      step === "forgotOtp" ||
      step === "forgotReset"
    ) {
      
      return "Forgot Password";
    }

    return "Log In or Sign Up";
  
  };

  const getHeaderSubtitle = () => {
    if (step === "otp") {
      return loginMethod === "email"
        ? `Enter the one-time password sent to ${email}.`
        : "Enter the one-time password sent to your registered email.";
    }

    if (step === "forgotEmail") {
      return "Enter your account email to receive a password reset OTP.";
    }

    if (step === "forgotOtp") {
      return `Enter the one-time password sent to ${forgotEmail}.`;
    }

    if (step === "forgotReset") {
      return "Create a new password for your account.";
    }

    if (authMode === "password") {
      return `Use your ${
        loginMethod === "email" ? "email" : "mobile number"
      } and password to sign in.`;
    }

    return `Use your ${
      loginMethod === "email" ? "email" : "mobile number"
    } to receive a one-time password or continue with Google.`;
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        maxWidth="860px"
        ariaLabel={step === "entry" ? "Log in or sign up" : "Authentication"}
        panelClassName="modal-panel--overlay-close login-popup-modal"
        contentClassName="modal-content--flush login-popup-modal__content"
      >
        <div className="login-popup">
          <div className="login-popup__promo">
            <div>
              <img
                src={loginimage}
                alt="login-image"
                className="login-popup__promo-image"
              />
            </div>
          </div>

          <div className="login-popup__panel">
            <div className="login-popup__panel-inner">
              <div className="login-popup__header">
                {step !== "entry" ? (
                  <button
                    type="button"
                    className="login-popup__back"
                    onClick={handleBack}
                    aria-label="Go back"
                  >
                    <FiArrowLeft aria-hidden="true" />
                    <span>Back</span>
                  </button>
                ) : (
                  <p className="login-popup__eyebrow">Secure access</p>
                )}

                <h2 className="login-popup__title">{getHeaderTitle()}</h2>
                <p className="login-popup__subtitle">{getHeaderSubtitle()}</p>
              </div>

              {step === "entry" && (
                <div className="login-popup__form">
                  <div
                    className="login-popup__auth-toggle"
                    role="tablist"
                    aria-label="Login mode"
                  >
                    <button
                      type="button"
                      className={`login-popup__auth-toggle-btn ${
                        authMode === "otp" ? "is-active" : ""
                      }`}
                      onClick={() => handleAuthModeChange("otp")}
                    >
                      Login with OTP
                    </button>
                    <button
                      type="button"
                      className={`login-popup__auth-toggle-btn ${
                        authMode === "password" ? "is-active" : ""
                      }`}
                      onClick={() => handleAuthModeChange("password")}
                    >
                      Login with Password
                    </button>
                  </div>

                  {loginMethod === "email" ? (
                    <div className="login-popup__field">
                      <label
                        className="login-popup__label"
                        htmlFor="login-email"
                      >
                        Email ID
                      </label>
                      <input
                        id="login-email"
                        className="login-popup__input"
                        type="email"
                        value={email}
                        placeholder="Enter your email ID"
                        autoComplete="email"
                        inputMode="email"
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                  ) : (
                    <div className="login-popup__field">
                      <label
                        className="login-popup__label"
                        htmlFor="login-mobile"
                      >
                        Mobile Number
                      </label>
                      <input
                        id="login-mobile"
                        className="login-popup__input"
                        type="tel"
                        value={mobile}
                        placeholder="Enter your mobile number"
                        autoComplete="tel"
                        inputMode="numeric"
                        maxLength="10"
                        onChange={(e) =>
                          setMobile(e.target.value.replace(/\D/g, ""))
                        }
                      />
                    </div>
                  )}

                  {authMode === "password" && (
                    <div className="login-popup__field">
                      <label
                        className="login-popup__label"
                        htmlFor="login-password"
                      >
                        Password
                      </label>
                      <input
                        id="login-password"
                        className="login-popup__input"
                        type="password"
                        value={password}
                        placeholder="Enter your password"
                        autoComplete="current-password"
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                  )}

                  <button
                    type="button"
                    className="login-popup__primary-btn modal-button modal-button--primary"
                    onClick={
                      authMode === "otp" ? handleSendOtp : handlePasswordLogin
                    }
                    disabled={authMode === "otp" ? sending : loggingIn}
                  >
                    {authMode === "otp"
                      ? sending
                        ? "Sending..."
                        : "Continue"
                      : loggingIn
                        ? "Logging in..."
                        : "Log In"}
                  </button>

                  {authMode === "password" && (
                    <div className="login-popup__helper-row">
                      <button
                        type="button"
                        className="login-popup__link"
                        onClick={handleForgotPasswordClick}
                      >
                        Forgot Password?
                      </button>
                    </div>
                  )}

                  <div className="login-popup__method-toggle">
                    <span className="login-popup__method-toggle-text">
                      {loginMethod === "email"
                        ? ""
                        : "Login with email?"}
                    </span>
                    <button
                      type="button"
                      className="login-popup__method-toggle-btn"
                      onClick={toggleLoginMethod}
                    >
                      {loginMethod === "email" ? "" : "Use Email"}
                    </button>
                  </div>

                  <div className="login-popup__divider" aria-hidden="true">
                    <span>or continue with</span>
                  </div>

                  <button
                    type="button"
                    className="login-popup__provider-btn"
                    onClick={handleGoogleLoginClick}
                    disabled={googleLoading}
                  >
                    <img
                      src="https://cdn-icons-png.flaticon.com/512/281/281764.png"
                      alt=""
                      className="login-popup__provider-icon"
                    />
                    {googleLoading ? "Redirecting..." : "Continue with Google"}
                  </button>

                  <div className="login-popup__footer">
                    <p className="login-popup__signup">
                      <span>Don't have an account?</span>
                      <button
                        type="button"
                        className="login-popup__link"
                        onClick={clicksignup}
                      >
                        Sign Up
                      </button>
                    </p>
                    <p className="login-popup__terms">
                      By continuing, you agree to Elumia Med Store{" "}
                      <u>Terms of Use</u> and <u>Privacy Policy</u>.
                    </p>
                  </div>
                </div>
              )}

              {step === "otp" && (
                <div className="login-popup__form">
                  <div className="login-popup__field">
                    <label
                      className="login-popup__label"
                      htmlFor="login-identifier"
                    >
                      {loginMethod === "email" ? "Email ID" : "Mobile Number"}
                    </label>
                    <input
                      id="login-identifier"
                      className="login-popup__input login-popup__input--muted"
                      type={loginMethod === "email" ? "email" : "tel"}
                      value={loginMethod === "email" ? email : mobile}
                      disabled
                    />
                  </div>

                  <div className="login-popup__otp-row">
                    <label className="login-popup__label" htmlFor="login-otp">
                      Enter OTP
                    </label>
                    <span className="login-popup__timer" aria-live="polite">
                      OTP expires in {timer}s
                    </span>
                  </div>

                  <input
                    id="login-otp"
                    className="login-popup__otp-input"
                    type="text"
                    maxLength="6"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                  />

                  <button
                    type="button"
                    className="login-popup__primary-btn modal-button modal-button--primary"
                    onClick={handleVerifyOtp}
                    disabled={verifying}
                  >
                    {verifying ? "Verifying..." : "Verify"}
                  </button>

                  <button
                    type="button"
                    className="login-popup__secondary-btn modal-button modal-button--secondary"
                    onClick={handleResendOtp}
                    disabled={sending}
                  >
                    {sending ? "Resending..." : "Resend OTP"}
                  </button>
                </div>
              )}

              {step === "forgotEmail" && (
                <div className="login-popup__form">
                  <div className="login-popup__field">
                    <label
                      className="login-popup__label"
                      htmlFor="forgot-password-email"
                    >
                      Email ID
                    </label>
                    <input
                      id="forgot-password-email"
                      className="login-popup__input"
                      type="email"
                      value={forgotEmail}
                      placeholder="Enter your email ID"
                      autoComplete="email"
                      inputMode="email"
                      onChange={(e) => setForgotEmail(e.target.value)}
                    />
                  </div>

                  <button
                    type="button"
                    className="login-popup__primary-btn modal-button modal-button--primary"
                    onClick={handleSendForgotPasswordOtp}
                    disabled={forgotSending}
                  >
                    {forgotSending ? "Sending..." : "Send OTP"}
                  </button>
                </div>
              )}

              {step === "forgotOtp" && (
                <div className="login-popup__form">
                  <div className="login-popup__field">
                    <label
                      className="login-popup__label"
                      htmlFor="forgot-password-email-readonly"
                    >
                      Email ID
                    </label>
                    <input
                      id="forgot-password-email-readonly"
                      className="login-popup__input login-popup__input--muted"
                      type="email"
                      value={forgotEmail}
                      disabled
                    />
                  </div>

                  <div className="login-popup__otp-row">
                    <label
                      className="login-popup__label"
                      htmlFor="forgot-password-otp"
                    >
                      Enter OTP
                    </label>
                    <span className="login-popup__timer" aria-live="polite">
                      OTP expires in {timer}s
                    </span>
                  </div>

                  <input
                    id="forgot-password-otp"
                    className="login-popup__otp-input"
                    type="text"
                    maxLength="6"
                    placeholder="Enter OTP"
                    value={forgotOtp}
                    onChange={(e) =>
                      setForgotOtp(
                        e.target.value.replace(/\D/g, "").slice(0, 6),
                      )
                    }
                  />

                  <button
                    type="button"
                    className="login-popup__primary-btn modal-button modal-button--primary"
                    onClick={handleVerifyForgotPasswordOtp}
                    disabled={forgotVerifying}
                  >
                    {forgotVerifying ? "Verifying..." : "Verify OTP"}
                  </button>

                  <button
                    type="button"
                    className="login-popup__secondary-btn modal-button modal-button--secondary"
                    onClick={handleResendForgotPasswordOtp}
                    disabled={forgotSending}
                  >
                    {forgotSending ? "Resending..." : "Resend OTP"}
                  </button>
                </div>
              )}

              {step === "forgotReset" && (
                <div className="login-popup__form">
                  <div className="login-popup__field">
                    <label
                      className="login-popup__label"
                      htmlFor="reset-password-email-readonly"
                    >
                      Email ID
                    </label>
                    <input
                      id="reset-password-email-readonly"
                      className="login-popup__input login-popup__input--muted"
                      type="email"
                      value={forgotEmail}
                      disabled
                    />
                  </div>

                  <div className="login-popup__field">
                    <label
                      className="login-popup__label"
                      htmlFor="reset-password-new"
                    >
                      New Password
                    </label>
                    <input
                      id="reset-password-new"
                      className="login-popup__input"
                      type="password"
                      value={forgotNewPassword}
                      placeholder="Enter new password"
                      autoComplete="new-password"
                      onChange={(e) => setForgotNewPassword(e.target.value)}
                    />
                  </div>

                  <div className="login-popup__field">
                    <label
                      className="login-popup__label"
                      htmlFor="reset-password-confirm"
                    >
                      Confirm Password
                    </label>
                    <input
                      id="reset-password-confirm"
                      className="login-popup__input"
                      type="password"
                      value={forgotConfirmPassword}
                      placeholder="Confirm new password"
                      autoComplete="new-password"
                      onChange={(e) => setForgotConfirmPassword(e.target.value)}
                    />
                  </div>

                  <p className="login-popup__field-note">
                    Password must include letters, numbers and a special
                    character.
                  </p>

                  <button
                    type="button"
                    className="login-popup__primary-btn modal-button modal-button--primary"
                    onClick={handleResetPassword}
                    disabled={resettingPassword}
                  >
                    {resettingPassword ? "Resetting..." : "Reset Password"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <PhoneKycPopup
        isOpen={kycOpen}
        onClose={() => setKycOpen(false)}
        email={email}
      />
    </>
  );
}

export default LoginPopup;
